import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, Trash2, Edit2, Users, Eye, ArrowLeft, X, Calendar, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewRegs, setViewRegs] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', event_type: 'community', date: '', time: '', location: '', capacity: '', image_url: '' });
  const navigate = useNavigate();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) { navigate('/admin/login'); return; }
    fetchEvents();
  }, [navigate, token]);

  const fetchEvents = async () => {
    try {
      const res = await fetch(`${API}/api/events`);
      const data = await res.json();
      setEvents(data.events || []);
      setLoading(false);
    } catch { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (!form.title || !form.date || !form.time || !form.location) {
      toast.error('Title, date, time and location are required');
      return;
    }
    const body = { ...form, capacity: form.capacity ? parseInt(form.capacity) : null };
    if (!body.image_url) delete body.image_url;

    try {
      const url = editing ? `${API}/api/events/${editing}` : `${API}/api/events`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (res.status === 401) { localStorage.removeItem('admin_token'); navigate('/admin/login'); return; }
      if (!res.ok) { const d = await res.json(); toast.error(d.detail || 'Failed'); return; }
      toast.success(editing ? 'Event updated' : 'Event created');
      setShowForm(false);
      setEditing(null);
      setForm({ title: '', description: '', event_type: 'community', date: '', time: '', location: '', capacity: '', image_url: '' });
      fetchEvents();
    } catch { toast.error('Network error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event and all its registrations?')) return;
    try {
      const res = await fetch(`${API}/api/events/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { toast.error('Failed to delete'); return; }
      toast.success('Event deleted');
      fetchEvents();
    } catch { toast.error('Network error'); }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'cancelled' : 'active';
    try {
      await fetch(`${API}/api/events/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus })
      });
      toast.success(`Event ${newStatus === 'active' ? 'activated' : 'cancelled'}`);
      fetchEvents();
    } catch { toast.error('Failed'); }
  };

  const handleViewRegs = async (id) => {
    try {
      const res = await fetch(`${API}/api/events/${id}/registrations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setRegistrations(data.registrations || []);
      setViewRegs(id);
    } catch { toast.error('Failed to load registrations'); }
  };

  const startEdit = (event) => {
    setForm({
      title: event.title,
      description: event.description,
      event_type: event.event_type,
      date: event.date,
      time: event.time,
      location: event.location,
      capacity: event.capacity ? String(event.capacity) : '',
      image_url: event.image_url || ''
    });
    setEditing(event._id);
    setShowForm(true);
  };

  if (loading) return <div className="min-h-screen pt-20 flex items-center justify-center"><p className="text-lg text-gray-600">Loading...</p></div>;

  return (
    <div className="min-h-screen pt-20 bg-gray-50" data-testid="admin-events-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <Link to="/admin" className="text-sm text-gray-500 hover:text-[#E6B933] flex items-center gap-1 mb-2"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="admin-events-title">Manage Events</h1>
            <p className="text-gray-500 mt-1">{events.length} event{events.length !== 1 ? 's' : ''} total</p>
          </div>
          <Button onClick={() => { setShowForm(true); setEditing(null); setForm({ title: '', description: '', event_type: 'community', date: '', time: '', location: '', capacity: '', image_url: '' }); }} className="bg-[#E6B933] hover:bg-[#D4A01F] text-black font-bold" data-testid="create-event-btn">
            <Plus className="w-4 h-4 mr-2" /> Create Event
          </Button>
        </div>

        {/* Create/Edit Form */}
        {showForm && (
          <Card className="p-6 mb-8 border-2 border-[#E6B933]/30" data-testid="event-form">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{editing ? 'Edit Event' : 'Create New Event'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }}><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Title *</Label>
                <Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="Event title" className="mt-1" data-testid="event-title-input" />
              </div>
              <div>
                <Label className="text-sm font-medium">Type *</Label>
                <select value={form.event_type} onChange={e => setForm(f => ({...f, event_type: e.target.value}))} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" data-testid="event-type-select">
                  <option value="community">Community</option>
                  <option value="volunteer">Volunteer</option>
                </select>
              </div>
              <div>
                <Label className="text-sm font-medium">Date *</Label>
                <Input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} className="mt-1" data-testid="event-date-input" />
              </div>
              <div>
                <Label className="text-sm font-medium">Time *</Label>
                <Input value={form.time} onChange={e => setForm(f => ({...f, time: e.target.value}))} placeholder="e.g. 10:00 AM - 2:00 PM" className="mt-1" data-testid="event-time-input" />
              </div>
              <div>
                <Label className="text-sm font-medium">Location *</Label>
                <Input value={form.location} onChange={e => setForm(f => ({...f, location: e.target.value}))} placeholder="Address or venue" className="mt-1" data-testid="event-location-input" />
              </div>
              <div>
                <Label className="text-sm font-medium">Capacity (optional)</Label>
                <Input type="number" value={form.capacity} onChange={e => setForm(f => ({...f, capacity: e.target.value}))} placeholder="Leave blank for unlimited" className="mt-1" data-testid="event-capacity-input" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm font-medium">Description *</Label>
                <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Describe the event..." rows={3} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" data-testid="event-desc-input" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm font-medium">Image URL (optional)</Label>
                <Input value={form.image_url} onChange={e => setForm(f => ({...f, image_url: e.target.value}))} placeholder="https://..." className="mt-1" data-testid="event-image-input" />
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <Button onClick={handleSubmit} className="bg-[#E6B933] hover:bg-[#D4A01F] text-black font-bold" data-testid="event-save-btn">
                {editing ? 'Update Event' : 'Create Event'}
              </Button>
              <Button variant="outline" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</Button>
            </div>
          </Card>
        )}

        {/* Registrations Modal */}
        {viewRegs && (
          <Card className="p-6 mb-8 border-2 border-blue-200" data-testid="registrations-panel">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Registrations ({registrations.length})</h2>
              <button onClick={() => setViewRegs(null)}><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
            </div>
            {registrations.length === 0 ? (
              <p className="text-gray-500">No registrations yet.</p>
            ) : (
              <div className="space-y-3">
                {registrations.map((r, i) => (
                  <div key={i} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                    <div>
                      <p className="font-medium text-gray-900">{r.name}</p>
                      <p className="text-sm text-gray-500">{r.email} {r.phone && `· ${r.phone}`}</p>
                      {r.message && <p className="text-sm text-gray-400 mt-1">{r.message}</p>}
                    </div>
                    <span className="text-xs text-gray-400">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Events List */}
        {events.length === 0 ? (
          <Card className="p-12 text-center">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Events Yet</h3>
            <p className="text-gray-500">Create your first event to get started.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {events.sort((a, b) => new Date(b.date) - new Date(a.date)).map(event => (
              <Card key={event._id} className={`p-5 ${event.status === 'cancelled' ? 'opacity-60' : ''}`} data-testid={`admin-event-${event._id}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-bold text-gray-900">{event.title}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase ${event.event_type === 'volunteer' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{event.event_type}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase ${event.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>{event.status}</span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{new Date(event.date).toLocaleDateString()}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{event.time}</span>
                      <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{event.location}</span>
                      <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{event.registration_count || 0}{event.capacity ? ` / ${event.capacity}` : ''} registered</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => handleViewRegs(event._id)} data-testid={`view-regs-${event._id}`}><Eye className="w-4 h-4 mr-1" /> Registrations</Button>
                    <Button size="sm" variant="outline" onClick={() => startEdit(event)} data-testid={`edit-event-${event._id}`}><Edit2 className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => handleStatusToggle(event._id, event.status)} className={event.status === 'active' ? 'text-orange-500 border-orange-300' : 'text-green-500 border-green-300'}>
                      {event.status === 'active' ? 'Cancel' : 'Activate'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(event._id)} className="text-red-500 border-red-300" data-testid={`delete-event-${event._id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEvents;
